import type { FactsetChartFormat } from "./chartCreatorTypes";

export class FactsetChartFormatCoerceError extends Error {
  readonly code = "active_graph_invalid_format";

  constructor(message: string) {
    super(message);
    this.name = "FactsetChartFormatCoerceError";
    Object.setPrototypeOf(this, FactsetChartFormatCoerceError.prototype);
  }
}

const RGB_PATTERN =
  /^RGB\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
const HEX_PATTERN = /^#?([0-9A-Fa-f]{6})$/;
const SERIES_INDEX_PATTERN = /^\d{1,2}$/;
const SERIES_COLOR_KEY_PATTERN = /^seriescolor(\d{1,2})$/i;
const MAX_SERIES_COLOR_INDEX = 15;

export function hexToRgb(hex: string): string | null {
  const match = HEX_PATTERN.exec(hex.trim());
  if (!match) return null;
  const n = Number.parseInt(match[1], 16);
  return `RGB(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255})`;
}

export function coerceFactsetColor(value: string, field: string): string {
  const trimmed = value.trim();
  const rgb = RGB_PATTERN.exec(trimmed);
  if (rgb) {
    const channels = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    if (
      channels.some(
        (channel) => !Number.isInteger(channel) || channel < 0 || channel > 255,
      )
    ) {
      throw new FactsetChartFormatCoerceError(
        `${field} must use RGB(r,g,b) with channels from 0 to 255.`,
      );
    }
    return `RGB(${channels[0]},${channels[1]},${channels[2]})`;
  }
  const converted = hexToRgb(trimmed);
  if (converted) return converted;
  throw new FactsetChartFormatCoerceError(
    `${field} must use RGB(r,g,b) with channels from 0 to 255.`,
  );
}

export function canonicalizeSeriesColorKey(key: string): string {
  const trimmed = key.trim();
  if (SERIES_INDEX_PATTERN.test(trimmed)) {
    const index = Number(trimmed);
    if (index >= 0 && index <= MAX_SERIES_COLOR_INDEX) {
      return `seriesColor${index}`;
    }
  }
  const named = SERIES_COLOR_KEY_PATTERN.exec(trimmed);
  if (named) {
    const index = Number(named[1]);
    if (index >= 0 && index <= MAX_SERIES_COLOR_INDEX) {
      return `seriesColor${index}`;
    }
  }
  return trimmed;
}

function normalizeColorMap(
  map: Record<string, string | undefined> | undefined,
  field: string,
  canonicalizeSeriesKeys: boolean,
): Record<string, string> | undefined {
  if (!map) return undefined;
  const next: Record<string, string> = {};
  for (const [key, value] of Object.entries(map)) {
    if (value === undefined) continue;
    const canonicalValue = coerceFactsetColor(value, `${field}.${key}`);
    const canonicalKey = canonicalizeSeriesKeys
      ? canonicalizeSeriesColorKey(key)
      : key;
    const existing = next[canonicalKey];
    if (existing !== undefined && existing !== canonicalValue) {
      throw new FactsetChartFormatCoerceError(
        `${field} has conflicting values for ${canonicalKey}.`,
      );
    }
    next[canonicalKey] = canonicalValue;
  }
  return next;
}

export function normalizeFactsetChartFormat(
  format: FactsetChartFormat,
): FactsetChartFormat {
  const next: FactsetChartFormat = { ...format };
  if (format.axisColor) {
    next.axisColor = normalizeColorMap(
      format.axisColor,
      "chartFormat.axisColor",
      false,
    );
  }
  if (format.fontColor) {
    next.fontColor = normalizeColorMap(
      format.fontColor,
      "chartFormat.fontColor",
      false,
    );
  }
  if (format.seriesColor) {
    next.seriesColor = normalizeColorMap(
      format.seriesColor,
      "chartFormat.seriesColor",
      true,
    );
  }
  return next;
}
